import { createTheme } from "@mui/material/styles"
import { createSvgIcon } from "@mui/material/utils"
import { defaultTheme } from "components/Theme/Style"

// Charte epita.fr (2026)
const INK = '#1C1C1C'
const PRIMARY = '#2E3C9F'
const LIME = '#D6FF62'
const LIME_HOVER = '#C3E859'
const GREY_BG = '#F3F3F3'
const LINE = '#E8E8E8'
const DISABLED_BORDER = '#BDBDBD'
const DISABLED_TEXT = '#8A8A8A'
const ERROR = '#D32F2F'

// Les polices sont chargées par epita.fr : les @font-face du document
// s'appliquent aussi dans le shadow DOM des web components
const HEADING_FONT = "'Space Grotesk', 'Raleway', sans-serif"
const BODY_FONT = "'Raleway', 'Helvetica Neue', Arial, sans-serif"

// defaultTheme est un thème déjà construit : chaque variante y porte sa propre
// police (Roboto), changer typography.fontFamily ne suffit donc pas
const bodyVariants = Object.fromEntries(
  ['body1', 'body2', 'subtitle1', 'subtitle2', 'caption', 'overline'].map(variant => [variant, { fontFamily: BODY_FONT }])
)

// Cases à cocher à angles droits (les icônes MUI par défaut sont arrondies)
const SquareCheckboxIcon = createSvgIcon(
  <path d="M19 5v14H5V5h14m2-2H3v18h18V3z" />,
  'SquareCheckbox'
)
const SquareCheckboxCheckedIcon = createSvgIcon(
  <path d="M21 3H3v18h18V3zm-11 14-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />,
  'SquareCheckboxChecked'
)

// Flèche du site (arrow-up-right-new.svg, 15 px), en data URI pour les pseudo-éléments
const arrow = color =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='15' height='15' viewBox='0 0 15 15' fill='none'><path d='M1 1H14.3333V14.3333M14.3333 1L1 14.3333' stroke='${color}' stroke-linecap='round' stroke-linejoin='round'/></svg>`
  )}")`

// Reproduit .btn-icon-swap--diagonal du site : une fenêtre de 45 × 15 px
// (30 px de marge + la flèche) qui masque tout ce qui en sort. Au survol,
// l'originale part de 35 px en haut à droite pendant que la copie arrive
// du bas à gauche, en 0,3 s.
const arrowSwap = color => ({
  content: '""',
  width: 45,
  height: 15,
  backgroundImage: `${arrow(color)}, ${arrow(color)}`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '15px 15px',
  backgroundPosition: '30px 0, -5px 35px',
  transition: 'background-position 0.3s',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
})

// Bouton principal : bouton bordé + carré citron avec flèche (CTA du site)
const containedPrimary = {
  position: 'relative',
  justifyContent: 'flex-start',
  color: INK,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${INK}`,
  padding: '0 61px 0 16px',
  // La flèche du carré remplace les icônes de fin (Send, NavigateNext…)
  '& .MuiButton-endIcon': { display: 'none' },
  // Carré citron
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 48,
    borderLeft: `1px solid ${INK}`,
    backgroundColor: LIME,
    transition: 'background-color 0.3s'
  },
  // Flèche, centrée dans le carré
  '&::after': {
    ...arrowSwap(INK),
    position: 'absolute',
    top: '50%',
    right: 16,
    marginTop: '-7.5px'
  },
  '&:hover': {
    backgroundColor: '#FFFFFF',
    '&::before': { backgroundColor: LIME_HOVER },
    '&::after': { backgroundPosition: '65px -35px, 30px 0' }
  },
  '&.Mui-disabled': {
    color: DISABLED_TEXT,
    backgroundColor: '#FFFFFF',
    borderColor: DISABLED_BORDER,
    '&::before': { backgroundColor: LINE, borderLeftColor: DISABLED_BORDER },
    '&::after': { backgroundImage: `${arrow(DISABLED_TEXT)}, ${arrow(DISABLED_TEXT)}` }
  }
}

// LoadingButton (@mui/lab) : le bouton en chargement est aussi disabled, on garde
// donc l'aspect actif et le loader prend la place de la flèche dans le carré
const containedPrimaryLoading = {
  '&.MuiButton-containedPrimary.MuiLoadingButton-loading': {
    color: INK,
    borderColor: INK,
    '&::before': { backgroundColor: LIME, borderLeftColor: INK },
    '&::after': { backgroundImage: 'none' }
  },
  '&.MuiButton-containedPrimary .MuiLoadingButton-loadingIndicator': {
    left: 'auto',
    right: 0,
    top: 0,
    bottom: 0,
    width: 48,
    transform: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: INK
  }
}

const theme = createTheme(defaultTheme, {
  palette: {
    mode: 'light',
    primary: { main: PRIMARY, light: '#727BBE', dark: '#1C245F', contrastText: '#FFFFFF' },
    secondary: { main: LIME, light: '#ECFFB7', dark: '#98B546', contrastText: INK },
    neutral: { main: PRIMARY },
    text: { primary: INK, secondary: '#5C5C5C' },
    divider: LINE,
    background: { default: GREY_BG, paper: '#FFFFFF' },
    error: { main: ERROR }
  },
  typography: {
    fontFamily: BODY_FONT,
    ...bodyVariants,
    h1: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.05em' },
    h2: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.03em' },
    h3: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.03em' },
    h4: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.03em' },
    h5: { fontFamily: HEADING_FONT, fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontFamily: HEADING_FONT, fontWeight: 600, letterSpacing: '-0.01em' },
    button: { fontFamily: BODY_FONT, fontWeight: 700, fontSize: '1rem', textTransform: 'none' }
  },
  shape: { borderRadius: 0 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          minHeight: 48,
          padding: '0 20px',
          transition: 'background-color 0.3s, border-color 0.3s, color 0.3s',
          // Focus clavier du site : contour noir 3 px + anneau blanc 6 px
          '&.Mui-focusVisible': {
            outline: '3px solid #000000',
            outlineOffset: 1,
            boxShadow: '0 0 0 6px #FFFFFF'
          }
        },
        containedPrimary,
        outlinedPrimary: {
          color: INK,
          backgroundColor: '#FFFFFF',
          borderColor: INK,
          '&:hover': { backgroundColor: GREY_BG, borderColor: INK },
          '&.Mui-disabled': { color: DISABLED_TEXT, borderColor: DISABLED_BORDER }
        }
      }
    },
    MuiLoadingButton: {
      styleOverrides: {
        root: containedPrimaryLoading
      }
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: `1px solid ${LINE}`
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-completed': { color: INK },
          '&.Mui-active': { color: PRIMARY }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: INK }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: INK }
        }
      }
    },
    // Astérisque des champs obligatoires : rouge et gras pour se détacher du libellé
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: ERROR, fontWeight: 700 }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <SquareCheckboxIcon />,
        checkedIcon: <SquareCheckboxCheckedIcon />
      },
      styleOverrides: {
        root: {
          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: INK }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: INK }
        }
      }
    },
    // Listes déroulantes : même fond au survol qu'à la navigation clavier
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover, &.Mui-focusVisible': { backgroundColor: LINE }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&.Mui-focused, &.Mui-focusVisible': { backgroundColor: `${LINE} !important` }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: LINE,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: DISABLED_BORDER }
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          // Filet de 3 px à gauche, couleur de la gravité (icône conservée)
          ...((ownerState.variant ?? 'standard') === 'standard' && {
            borderLeft: `3px solid ${theme.palette[ownerState.color ?? ownerState.severity ?? 'success'].main}`
          }),
          '& a': { color: 'inherit', textDecoration: 'underline' }
        })
      }
    }
  }
})

export default theme
